import { useEffect, useRef } from "react";
import { IoMdClose } from "react-icons/io";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { CompanyUrl } from "../../pages/Settings/DomainSettings";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { rerenderLayout } from "@/store/RenderProtectionRoute.slice";

interface EditDomainModalProps {
  currentDomain: string;
  onClose: () => void;
  setDomainName: React.Dispatch<React.SetStateAction<string>>;
}

const DomainModal: React.FC<EditDomainModalProps> = ({
  currentDomain,
  onClose,
  setDomainName,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const url = `${BASE_URL}/api/dashboard/settings/subdomain?locale=${lang}`;
  const dispatch  = useDispatch()
  const validationSchema = Yup.object({
    domainName: Yup.string()
      .matches(
        /^[A-Za-z][A-Za-z0-9-]*$/,
        t("domainModal.startWithLetter")  as string,
      )
      .min(
        3,
        t("domainModal.minLength")  as string,
      )
      .max(
        30,
        t("domainModal.maxLength") as string,
      )
      .required(
        t("domainModal.required") as string),
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  const handleApiSubmit = async (newDomain: string) => {
    const LoadingToast = toast.loading(
      t("domainModal.updating") as string,
    );
    try {
      const response = await axios.put(
        url,
        {
          subdomain: newDomain,
        },
        {
          headers: {
            Authorization: `${token}`,
          },
        },
      );

      if (!response.data.success) {
        toast.error(response.data.error);
        return;
      }

      toast.success(t("domainModal.updated") as string,
      );
      setDomainName(newDomain);
      onClose();
      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const errorMessage =
          error.response?.data?.error ||
          (t("domainModal.failed") as string)
        toast.error(errorMessage);
      } else {
        toast.error(
          t("domainModal.unexpectedError") as string,
        );
      }
      console.error(
        t("domainModal.errorUpdating")
      );
    } finally {
      toast.dismiss(LoadingToast);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/50 pt-[70px]"
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={modalRef}
        className="flex w-[95%] max-w-3xl flex-col gap-4 rounded-lg bg-white px-4 py-4 shadow-lg"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {t("domainModal.edit") as string}
          </h2>
          <button
            onClick={onClose}
            className="mb-3 text-gray-600 hover:text-gray-800"
          >
            <IoMdClose size={24} className="text-red-600" />
          </button>
        </div>

        {/* Formik Form */}
        <div>
          <Formik
            initialValues={{ domainName: currentDomain }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              await handleApiSubmit(values.domainName);
              setSubmitting(false);
              dispatch(rerenderLayout())
            }}
          >
            {({ isSubmitting }) => (
              <Form className="flex flex-col space-y-4">
                <div>
                  <label
                    htmlFor="domainName"
                    className="text-sm font-medium text-gray-700"
                  >
                    {t("domainModal.label")}
                  </label>
                  <div dir="ltr" className="relative h-12">
                    {/* Fixed Height Wrapper */}
                    <div className="absolute bottom-0 end-0 flex h-full w-[140px] items-center justify-center rounded-e-md border-s border-gray-300 px-2 text-sm">
                      <p className="-full flex items-center">{CompanyUrl}</p>
                    </div>
                    {/* Fixed Height Input */}
                    <Field
                      id="domainName"
                      name="domainName"
                      placeholder={
                        t("domainModal.placeholder") as string
                      }
                      className="h-full w-full rounded-md border border-gray-300 pe-[145px] ps-4"
                    />
                  </div>
                  {/* Error Message */}
                  <ErrorMessage
                    name="domainName"
                    component="div"
                    className="mt-1 self-end text-sm text-red-600"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-fit self-end rounded-md bg-sidebar px-4 py-2 text-sm font-semibold text-skyblue ${isSubmitting ? "cursor-not-allowed opacity-50" : ""
                    }`}
                >
                  {
                    t(isSubmitting ? "domainModal.saving" : "save") as string
                  }
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default DomainModal;
